/**
 * SEO helpers — canonical/hreflang URL builders and JSON-LD generators.
 *
 * Canonical/site URLs are derived at RUNTIME from `window.location.origin`
 * so the same React bundle works across all 7 domains (multi-tenant setup).
 * Falls back to REACT_APP_BACKEND_URL when rendered server-side / during build.
 */

const ENV_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

const runtimeOrigin = () => {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return ENV_URL;
};

export const SITE_NAME = "Mallorca Digital & Tech Services";
export const LOCALES = ["de", "en", "es"];
export const OG_LOCALE_MAP = { de: "de_DE", en: "en_US", es: "es_ES" };

export const absoluteUrl = (path = "/") => {
  const base = runtimeOrigin();
  if (!path) return base || "/";
  if (/^https?:\/\//i.test(path)) return path;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
};

// Backwards-compat export for any caller that imports SITE_URL.
export const SITE_URL = runtimeOrigin();

/** Build hreflang alternate links for a given path. We use `?lang=` for non-default locales. */
export const buildHreflangAlternates = (path) => {
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return LOCALES.map((loc) => ({
    hrefLang: loc,
    href: loc === "de" ? absoluteUrl(cleanPath) : absoluteUrl(`${cleanPath}?lang=${loc}`),
  })).concat([{ hrefLang: "x-default", href: absoluteUrl(cleanPath) }]);
};

/** Schema.org Service JSON-LD for the service detail pages. */
export const buildServiceJsonLd = ({ name, description, url, image, areaServed = "Mallorca, Spain" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name,
  description,
  url,
  image: image || undefined,
  provider: {
    "@type": "LocalBusiness",
    name: SITE_NAME,
    url: SITE_URL || undefined,
    areaServed,
    address: {
      "@type": "PostalAddress",
      addressRegion: "Illes Balears",
      addressCountry: "ES",
    },
  },
  areaServed,
});

/** FAQPage JSON-LD from a list of {q,a} entries. */
export const buildFaqJsonLd = (entries) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: (entries || []).map((e) => ({
    "@type": "Question",
    name: e.q,
    acceptedAnswer: { "@type": "Answer", text: e.a },
  })),
});

/** Organization / WebSite JSON-LD for the landing page. */
export const buildOrganizationJsonLd = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL || undefined,
  sameAs: [],
  areaServed: "Mallorca, Spain",
});

/**
 * LocalBusiness JSON-LD — strongest single signal we can give Google for local
 * SEO ranking ("Starlink Mallorca", "Smart Home Palma", etc.). Includes
 * service catalogue, area served, opening hours and contact info so the
 * business can surface in Maps / Knowledge Panel results.
 */
export const buildLocalBusinessJsonLd = ({
  phone = "+34657138915",
  email = "info@digital-mallorca.de",
} = {}) => ({
  "@context": "https://schema.org",
  "@type": ["LocalBusiness", "ProfessionalService"],
  "@id": `${SITE_URL || ""}#localbusiness`,
  name: SITE_NAME,
  url: SITE_URL || undefined,
  telephone: phone,
  email,
  priceRange: "€€–€€€",
  image: SITE_URL ? `${SITE_URL}/api/og.png` : undefined,
  description:
    "Premium Tech-Service auf Mallorca: Starlink-Installation, Premium-WLAN, Smart Home, IRL-Streaming-Setups, Webentwicklung und persönlicher Tech-Support auf Deutsch, Englisch und Spanisch.",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Palma",
    addressRegion: "Illes Balears",
    addressCountry: "ES",
  },
  areaServed: [
    { "@type": "City", name: "Palma de Mallorca" },
    { "@type": "City", name: "Pollença" },
    { "@type": "City", name: "Sóller" },
    { "@type": "City", name: "Artà" },
    { "@type": "City", name: "Santanyí" },
    { "@type": "City", name: "Cala d'Or" },
    { "@type": "AdministrativeArea", name: "Mallorca, Illes Balears, Spain" },
  ],
  geo: {
    "@type": "GeoCoordinates",
    latitude: 39.5696,
    longitude: 2.6502,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "19:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "10:00",
      closes: "14:00",
    },
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: phone,
    contactType: "customer service",
    availableLanguage: ["German", "English", "Spanish"],
    areaServed: "ES",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Tech-Services Mallorca",
    itemListElement: [
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Starlink Installation Mallorca" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Premium WLAN & Mesh-Netzwerke" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Smart Home & Sicherheit" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Webseiten, SEO & KI-Automation" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Creator & IRL-Streaming Setups inkl. Kameramann" } },
      { "@type": "Offer", itemOffered: { "@type": "Service", name: "Persönlicher Tech-Support auf Deutsch" } },
    ],
  },
});
